import { X } from "lucide-react";
import { useState } from "react";

import { Button } from "@workspace/web-ui/components/button";
import { Checkbox } from "@workspace/web-ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@workspace/web-ui/components/dialog";
import { Label } from "@workspace/web-ui/components/label";
import { Textarea } from "@workspace/web-ui/components/textarea";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { getPrescriptionMedicationByMedicationIdQueryOptions } from "../pages/medicals/api/queries";
import { useMedicationRefillRequestMutation } from "../pages/medicals/api/mutations/medications";
import { SideEffects } from "../pages/medicals/schemas";
import { toast } from "sonner";

export const MedicationRefillRequestModal = ({
  open,
  onClose,
  prescription_id,
  medication_id
}: {
  open: boolean;
  onClose: () => void;
  prescription_id: string;
  medication_id: string;
}) => {
  const [step, setStep] = useState(1);
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  const { data: prescriptionData, isLoading: prescriptionLoading } = useQuery(getPrescriptionMedicationByMedicationIdQueryOptions(prescription_id, medication_id))

  const medicationData = prescriptionData?.medication

  const { mutate: medicationRefillRequest, isPending: medicationRefillRequestLoading } = useMedicationRefillRequestMutation(prescription_id, medication_id)

  const { control, handleSubmit, register, watch, setValue } = useForm<SideEffects>({
    defaultValues: {
      nausea: false,
      diarrhea: false,
      constipation: false,
      headache: false,
      none: false,
      other_symptoms: "",
    },
  });

  const sideEffects = watch(); // Watch all form values

  const handleNoneChange = (checked: boolean) => {
    if (checked) {
      // If "none" is selected, deselect all other side effects
      setValue("nausea", false);
      setValue("diarrhea", false);
      setValue("constipation", false);
      setValue("headache", false);
    }
    setValue("none", checked);
  };

  const handleSideEffectChange = (field: keyof SideEffects, checked: boolean) => {
    if (field !== "none" && sideEffects?.none) {
      // If any side effect is selected, deselect "none"
      setValue("none", false);
    }
    setValue(field, checked);
  };

  const onSubmit = (data: SideEffects) => {
    medicationRefillRequest({
      side_effects: {
        ...data,
      }
    }, {
      onSuccess: (response) => {
        if (response.success) {
          toast.success("Refill Request Sent!");
          onClose();
        } else {
          toast.error("Refill Request Failed!");
        }
      },
      onError: () => {
        toast.error("Refill Request Failed!");
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <div
        className={` ${open ? "fixed inset-0 z-40 h-screen bg-black/5 backdrop-blur-sm" : ""} `}
      />
      <DialogContent className="max-w-md rounded-lg p-6 sm:max-w-lg [&>button]:hidden">
        <DialogHeader className="flex flex-col items-center justify-center mb-6">
          <div>
            <Button
              onClick={onClose}
              className="absolute right-4 top-4 size-8 rounded-full bg-[#0404040D] text-gray-400 hover:bg-[#0404040D] hover:text-gray-600"
            >
              <X className="h-6 w-6 text-[#363840]" />
            </Button>
          </div>
          <div className="">
            <DialogTitle className="mt-6 text-4xl font-semibold">
              Request medications
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* STEP 1: Select Medications */}
        {step === 1 && (
          <div>
            <div className="space-y-4">
              <h3 className="text-sm font-medium">MEDICATIONS</h3>
              {prescriptionLoading ? (
                <>
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="flex items-center justify-between animate-pulse">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <div className="flex items-start">
                            <div className="flex flex-col space-y-2">
                              <div className="h-4 w-48 bg-gray-300 rounded" />
                              <div className="h-4 w-24 bg-gray-300 rounded" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="h-4 w-16 bg-gray-300 rounded" />
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-between ">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col ">
                      <div className="flex items-start ">
                        <div className="flex flex-col space-y-2">
                          <Label htmlFor={`med-${medicationData?.medication_id}`} className="text-sm">
                            {medicationData?.medication?.name} {medicationData?.medication?.duration ? `(${medicationData?.medication?.duration} supply)` : null}
                          </Label>
                          <span className="inline-flex items-center">
                            {" "}
                            {medicationData?.medication?.quantity ? (
                              <h6 className="text-sm text-[#202020CC]">
                                Quantity: {medicationData?.medication?.quantity}{" "}
                              </h6>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-medium">$ {medicationData?.medication?.cost}</span>
                </div>
              )}

              <div className="pt-5">
                <Button
                  className="w-full text-base rounded-lg h-10 font-normal"
                  onClick={() => {
                    nextStep();
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Side Effect Screening */}
        {step === 2 && (
          <form onSubmit={handleSubmit(onSubmit)} id="refill-medication-form">
            <div className="">
              <h3 className="text-xl font-medium text-[#2D2F35]">
                Side Effect Screening
              </h3>
              <h6 className="my-2 text-sm text-[#202020CC]">
                Have you experienced any of these side effects?
              </h6>
              <div className="space-y-3">
                {["nausea", "diarrhea", "constipation", "headache"].map(
                  (effect, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-2"
                    >
                      <Label
                        htmlFor={`side_effects.${effect}`}
                        className="text-sm"
                      >
                        {effect.charAt(0).toUpperCase() + effect.slice(1)}
                      </Label>
                      <Controller
                        name={effect as keyof SideEffects}
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id={`side_effects.${effect}`}
                            checked={!!field.value}
                            onCheckedChange={(checked) =>
                              handleSideEffectChange(effect as keyof SideEffects, !!checked)
                            }
                          />
                        )}
                      />
                    </div>
                  )
                )}
                {/* None Option */}
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="side_effects.none" className="text-sm">
                    None
                  </Label>
                  <Controller
                    name="none"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="side_effects.none"
                        checked={field.value}
                        onCheckedChange={handleNoneChange}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <Label htmlFor="other_symptoms" className="my-2 text-sm">
                  Any other symptoms?
                </Label>
                <Textarea
                  {...register("other_symptoms")}
                  rows={6}
                  placeholder="Describe any other symptoms you have experienced"
                  className="placeholder:text-sm placeholder:font-normal"
                />
              </div>
              <div className="flex justify-end gap-3 pt-5 text-base font-normal">
                <Button variant="outline" onClick={prevStep}>
                  Back
                </Button>
                <Button type="submit" form="refill-medication-form" disabled={medicationRefillRequestLoading}> {medicationRefillRequestLoading ? "Submitting..." : "Request"}</Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

 